/**
 * Calendar Export Service
 * Generates .ics files from meal plans for universal calendar compatibility
 * Also handles Google Calendar integration via direct browser calls to Google's API
 */

import { createEvents, EventAttributes } from 'ics'
import { RecipeRecord } from '../types/recipe'
import { supabase } from '../lib/supabase'
import { clientLogger } from '../utils/clientLogger'

const GOOGLE_CALENDAR_API = 'https://www.googleapis.com/calendar/v3/calendars/primary/events'
const MAX_DESCRIPTION_LENGTH = 8000

export class CalendarExportService {
  /**
   * Generate .ics file content from assigned recipes
   */
  generateICSFile(recipes: RecipeRecord[]): string {
    const events: EventAttributes[] = recipes
      .filter(recipe => recipe.assigned_dates && recipe.assigned_dates.length > 0)
      .flatMap(recipe =>
        recipe.assigned_dates.map((dateStr: string) =>
          this.recipeToCalendarEvent(recipe, dateStr)
        )
      )

    if (events.length === 0) {
      throw new Error('No assigned recipes to export')
    }

    const { error, value } = createEvents(events)

    if (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to generate .ics file')
      throw new Error(`Failed to generate calendar: ${error.message || error}`)
    }

    return value!
  }

  /**
   * Convert recipe to calendar event format
   */
  private recipeToCalendarEvent(
    recipe: RecipeRecord,
    dateStr: string
  ): EventAttributes {
    const date = new Date(dateStr)
    const mealTime = this.getMealStartTime(recipe.recipe_data.mealType)
    const duration = this.calculateDuration(recipe)

    // Build event
    return {
      start: [
        date.getFullYear(),
        date.getMonth() + 1, // ics library expects 1-based month
        date.getDate(),
        mealTime.hours,
        mealTime.minutes
      ],
      duration: { minutes: duration },
      title: `${this.capitalizeFirst(recipe.recipe_data.mealType)}: ${recipe.recipe_data.name}`,
      description: this.buildDescription(recipe),
      location: 'Kitchen',
      categories: [
        'Meal Planning',
        this.capitalizeFirst(recipe.recipe_data.mealType)
      ],
      status: 'CONFIRMED',
      busyStatus: 'FREE',
      organizer: { name: 'TalkToast', email: 'noreply@talktoast.com' },
      alarms: [
        {
          action: 'display',
          trigger: { minutes: 60, before: true },
          description: 'Time to start cooking!'
        }
      ]
    }
  }

  /**
   * Get default start time for meal type
   */
  private getMealStartTime(mealType: string): { hours: number; minutes: number } {
    const times: Record<string, { hours: number; minutes: number }> = {
      breakfast: { hours: 8, minutes: 0 },
      lunch: { hours: 12, minutes: 0 },
      dinner: { hours: 18, minutes: 0 },
      snacks: { hours: 15, minutes: 0 },
      dessert: { hours: 20, minutes: 0 }
    }
    return times[mealType?.toLowerCase()] || { hours: 12, minutes: 0 }
  }

  /**
   * Calculate event duration based on prep + cook time
   */
  private calculateDuration(recipe: RecipeRecord): number {
    const prepTime = recipe.recipe_data.prepTime || 0
    const cookTime = recipe.recipe_data.cookTime || 0
    const totalTime = prepTime + cookTime

    // Default to 30 minutes if no time specified
    return totalTime > 0 ? totalTime : 30
  }

  /**
   * Build comprehensive event description with recipe details
   */
  private buildDescription(recipe: RecipeRecord): string {
    const { recipe_data } = recipe
    let description = ''

    // Recipe description
    if (recipe_data.description) {
      description += `${recipe_data.description}\n\n`
    }

    // Timing and servings
    description += `⏱️ Prep: ${recipe_data.prepTime || 'N/A'} min | Cook: ${recipe_data.cookTime || 'N/A'} min\n`
    description += `👥 Servings: ${recipe_data.servings || 'N/A'}\n\n`

    // Ingredients list
    if (recipe_data.ingredients && recipe_data.ingredients.length > 0) {
      description += '📋 INGREDIENTS:\n'
      recipe_data.ingredients.forEach((ing: any) => {
        if (typeof ing === 'string') {
          description += `• ${ing}\n`
        } else {
          const amount = ing.amount || ''
          const unit = ing.unit || ''
          const name = ing.name || ing
          description += `• ${amount} ${unit} ${name}`.trim() + '\n'
        }
      })
      description += '\n'
    }

    // Instructions (optional, can be very long)
    if (recipe_data.instructions && recipe_data.instructions.length > 0) {
      description += '📝 INSTRUCTIONS:\n'
      recipe_data.instructions.forEach((instruction: string, index: number) => {
        description += `${index + 1}. ${instruction}\n`
      })
      description += '\n'
    }

    // Nutrition info
    if (recipe_data.nutrition?.calories) {
      description += `🔥 Calories: ${recipe_data.nutrition.calories}`
      if (recipe_data.nutrition.protein) {
        description += ` | Protein: ${recipe_data.nutrition.protein}g`
      }
      if (recipe_data.nutrition.carbs) {
        description += ` | Carbs: ${recipe_data.nutrition.carbs}g`
      }
      if (recipe_data.nutrition.fat) {
        description += ` | Fat: ${recipe_data.nutrition.fat}g`
      }
      description += '\n'
    }

    description += '\n---\nCreated with TalkToast'

    return description
  }

  /**
   * Build a Google Calendar event description, capped at MAX_DESCRIPTION_LENGTH
   * to stay within Google's API limits.
   */
  private buildGoogleDescription(recipe: RecipeRecord): string {
    const full = this.buildDescription(recipe)
    if (full.length <= MAX_DESCRIPTION_LENGTH) {
      return full
    }
    const truncated = full.slice(0, MAX_DESCRIPTION_LENGTH - 3)
    return truncated + '...'
  }

  /**
   * Validate that a string is a YYYY-MM-DD date and return it, or throw.
   */
  private validateDateString(dateStr: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      throw new Error(`Invalid date format: "${dateStr}". Expected YYYY-MM-DD.`)
    }
    return dateStr
  }

  /**
   * Capitalize first letter of string
   */
  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  /**
   * Trigger browser download of .ics file
   */
  downloadICSFile(icsContent: string, filename: string = 'meal-plan.ics'): void {
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Push a single recipe event directly to the Google Calendar REST API.
   * Called in the browser; the provider_token never leaves the client.
   */
  private async pushEventToGoogle(
    recipe: RecipeRecord,
    dateStr: string,
    providerToken: string
  ): Promise<void> {
    const date = this.validateDateString(dateStr)

    const startDate = new Date(date + 'T00:00:00Z')
    const endDate = new Date(startDate)
    endDate.setUTCDate(endDate.getUTCDate() + 1)
    const endDateStr = endDate.toISOString().slice(0, 10)

    const event = {
      summary: recipe.recipe_data.name,
      description: this.buildGoogleDescription(recipe),
      start: { date },
      end: { date: endDateStr }
    }

    const response = await fetch(GOOGLE_CALENDAR_API, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${providerToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      const message = (errorBody as any)?.error?.message || response.statusText
      throw new Error(`Google Calendar API error (${response.status}): ${message}`)
    }
  }

  /**
   * Push recipes to Google Calendar (requires Google OAuth).
   * All Google API calls happen directly in the browser — the provider_token
   * is never sent to the TalkToast backend.
   *
   * @param recipes - Array of recipes with assigned dates
   * @returns Promise that resolves when push is complete
   * @throws Error if not authenticated, not using Google OAuth, or token unavailable
   */
  async pushToGoogleCalendar(recipes: RecipeRecord[]): Promise<void> {
    // Get current user's session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    if (sessionError) {
      throw new Error('Failed to get authentication session')
    }

    if (!session) {
      throw new Error('Not authenticated. Please sign in to use this feature.')
    }

    // Check if user signed in with Google
    const provider = session.user.app_metadata?.provider
    if (provider !== 'google') {
      throw new Error('Please sign in with Google to use Google Calendar integration')
    }

    // Supabase only provides provider_token at the moment of OAuth sign-in;
    // it is null after a page refresh. Direct the user to re-authenticate.
    const providerToken = session.provider_token
    if (!providerToken) {
      throw new Error('Google access token not available. Please sign out and sign back in with Google to reconnect your calendar.')
    }

    // Filter recipes with assigned dates
    const assignedRecipes = recipes.filter(r =>
      r.assigned_dates && r.assigned_dates.length > 0
    )

    if (assignedRecipes.length === 0) {
      throw new Error('No assigned recipes to export')
    }

    // Push each recipe/date combination directly to Google's API from the browser
    let successCount = 0
    const errors: Array<{ recipe: string; date: string; error: string }> = []

    for (const recipe of assignedRecipes) {
      for (const dateStr of recipe.assigned_dates) {
        try {
          await this.pushEventToGoogle(recipe, dateStr, providerToken)
          successCount++
        } catch (err: any) {
          clientLogger.warn({ recipe: recipe.recipe_data.name, date: dateStr, error: err.message }, 'Failed to create event')
          errors.push({
            recipe: recipe.recipe_data.name,
            date: dateStr,
            error: err.message
          })
        }
      }
    }

    if (errors.length > 0) {
      clientLogger.warn({ errors }, 'Some events failed')
      if (successCount === 0) {
        throw new Error('Failed to create any calendar events. Please try again.')
      }
    }
  }
}

export const calendarExportService = new CalendarExportService()
export default calendarExportService
